/**
 * 创作页**来源条**的视觉件(2026-07-25 抽取)——「我从哪来、它替我定了什么、怎么换」。
 *
 *     [图标 / 缩略图] [来源类别 tag] [这一份具体是什么] [它替你定了什么] [换/清除]
 *
 * ═══ 为什么抽成共享件 ═══════════════════════════════════════════════════════════
 * 视频舱波1 与海报舱 V1 各自落了一条来源条,两边的 strip **逐字相同**(同一份设计稿
 * `_design-mock/*-create.html` 的 `.srcbar`)。两份拷贝的代价不是行数,是**会漂**:
 * 哪天改了一边的间距/描边,两个舱就长得不一样,而"两个舱壳语言一致"正是这两波要的东西。
 *
 * ★这里抽的只有**视觉**。各舱的来源枚举、判定优先级、每条来源说什么,都留在各自的
 *   `model/*Source.ts` —— 那是两个域的业务,合成一张表只会制造耦合
 *   (与「统一规格不硬套同一组件」同一个判断:统一的是规格语言,不是把域压平)。
 *
 * ★圆角走 `rounded-lg`(= `var(--radius)`,三皮肤 8/12/20 自动跟随),不写死 px。
 */
import { cn } from "@/shared/lib/utils";

export interface SourceStripProps {
  /** 来源类别(视觉上最小的一行,大写字距)。compact 下省略 —— 见下。 */
  tag?: string;
  /** 这一份具体是什么 */
  title: string;
  /** 它替你定了什么 */
  meta: string;
  /** 无缩略图时的占位图标 */
  icon: React.ReactNode;
  thumbUrl?: string | null;
  /**
   * 2026-07-28 — 规格 chip(如「3 张」「9:16+1:1+16:9」「约 2,100 积分」)。
   *
   * ★为什么加它:场景卡那条原本把「一句话用途 · 积分」拼成一行长 meta,用户要读完
   * 整句才知道会出几张、什么画幅。规格是**可扫**的信息,拼成句子就只能读。
   */
  chips?: Array<string | null | undefined>;
  /**
   * 紧凑态:图标**内联在标题前**,不占 44×44 的独立格子;省略 tag 行。
   *
   * ★场景卡的图标是 16px 的形制图示(SceneShapeGlyph,信息量在比例里),塞进 44px
   * 格子后四周全是空白,缩完只剩几个灰线框 —— 用户报的「页面上面那个丑」就是这个。
   * 图示与标题同行同量级才立得住。
   */
  compact?: boolean;
  /**
   * 2026-08-11(VID-CMP-06)—— meta 之下的**第二行留痕**(如「你选了 X,AI 本会判 Y」的分歧说明)。
   *
   * ★为什么不并进 meta:meta 是单行 truncate,可扫不可读 —— 而留痕行的价值恰恰在被完整读到,
   *   截掉尾巴的「保留你的选择;不合适可换档」就只剩前半句指责。可选属性,既有调用(海报舱等)
   *   零改动零回归。
   */
  detail?: string;
  /** 换 / 清除 / 退出 —— 由各舱自己决定给不给(给不出真动作就别给按钮) */
  action?: React.ReactNode;
  /** 带来源(非自由创作)用主色描边:一眼看出「这页不是空白开始的」。 */
  accent?: boolean;
  className?: string;
  /**
   * 各舱自己的 e2e 定位钩子属性名(如 `data-video-source-strip` / `data-poster-source`),
   * 值为空串。★保留各自的名字而不是统一成一个:两套 e2e 已经分别锁着它们,
   * 改名是纯churn,对用户零价值、对回归网有风险。
   */
  domainAttr?: string;
}

export function SourceStrip({
  tag,
  title,
  meta,
  icon,
  thumbUrl,
  chips,
  compact,
  detail,
  action,
  accent,
  className,
  domainAttr,
}: SourceStripProps) {
  const shownChips = (chips ?? []).filter(Boolean) as string[];
  return (
    <div
      {...(domainAttr ? { [domainAttr]: "" } : {})}
      className={cn(
        "flex items-center gap-3 rounded-lg border",
        compact ? "px-3 py-2" : "px-3.5 py-3",
        accent ? "border-primary/50 bg-primary/5" : "border-border/60 bg-card/40",
        className
      )}
    >
      {/* compact 下不占独立图标格 —— 图标改为内联在标题前(见 props 注释) */}
      {!compact &&
        (thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="h-11 w-[68px] shrink-0 rounded-sm border border-border object-cover"
          />
        ) : (
          <div
            className={cn(
              "grid h-11 w-11 shrink-0 place-items-center rounded-[calc(var(--radius)*0.75)]",
              accent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            )}
            aria-hidden
          >
            {icon}
          </div>
        ))}
      <div className="min-w-0 flex-1">
        {tag && !compact && (
          <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
            {tag}
          </div>
        )}
        <div className="flex min-w-0 items-center gap-2">
          {compact && (
            <span
              className={cn("shrink-0", accent ? "text-primary" : "text-muted-foreground")}
              aria-hidden
            >
              {icon}
            </span>
          )}
          <span className="truncate text-sm font-semibold text-foreground">{title}</span>
          {compact && meta && (
            <span className="hidden min-w-0 truncate text-2xs text-muted-foreground sm:inline">
              {meta}
            </span>
          )}
        </div>
        {!compact && <div className="truncate text-2xs text-muted-foreground">{meta}</div>}
        {detail && (
          /* 留痕行允许换行、不 truncate —— 被截断的留痕不算留痕(见 props 注释) */
          <div className="mt-0.5 text-2xs leading-snug text-muted-foreground">{detail}</div>
        )}
        {shownChips.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {shownChips.map((c) => (
              <span
                key={c}
                className={cn(
                  "rounded px-1.5 py-0.5 text-2xs font-medium",
                  accent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                )}
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
